import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  Factory, TrendingUp, TrendingDown, AlertTriangle, CheckCircle,
  Sparkles, Loader2, Target, Zap, Lightbulb, Tv, PlayCircle,
} from 'lucide-react'
import toast from 'react-hot-toast'
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from 'recharts'
import { api, formatMoney } from '@/lib/api'

interface DashboardData {
  periodo_dias: number
  status_counts: Record<string, number>
  kpis: {
    ops_concluidas: number
    custo_previsto_total: number
    custo_real_total: number
    desvio_pct: number | null
    quantidade_produzida: number
    quantidade_refugo: number
    taxa_refugo_pct: number | null
    ops_atrasadas: number
    propostas_pendentes: number
  }
  custo_breakdown: {
    mp: number
    mod: number
    maquina: number
    overhead: number
  }
  top_custo: Array<{ produto_id: number; codigo: string; descricao: string; custo_total: number; quantidade: number; custo_unit: number }>
  top_refugo: Array<{ produto_id: number; codigo: string; descricao: string; ok: number; refugo: number; taxa_refugo_pct: number }>
  alertas: Array<{
    tipo: string; severidade: string; titulo: string;
    descricao?: string; acao?: string; itens?: any[]
  }>
}

interface TendenciasData {
  meses: number
  serie: Array<{
    mes: string; ops: number;
    custo_previsto: number; custo_real: number;
    quantidade_produzida: number; quantidade_refugo: number
  }>
}

interface AnaliseIA {
  modelo: string
  analise: {
    resumo: string
    pontos_fortes?: string[]
    observacoes?: Array<{ titulo: string; detalhe: string; severidade: string }>
    oportunidades?: Array<{ titulo: string; descricao: string; impacto_estimado: string; esforco: string }>
    acoes_priorizadas?: Array<{ ordem: number; acao: string; motivo: string }>
  } | null
  texto_bruto: string | null
  parse_erro: string | null
  uso: { input_tokens?: number; output_tokens?: number; cache_creation_input_tokens?: number; cache_read_input_tokens?: number }
  contexto_resumo: { ops_analisadas: number; fichas_incompletas: number; mp_sem_custo: number }
}

const SEV_COR: Record<string, string> = {
  alta: 'border-rose-400 bg-rose-50 text-rose-900',
  media: 'border-amber-400 bg-amber-50 text-amber-900',
  baixa: 'border-sky-400 bg-sky-50 text-sky-900',
}

export default function DashboardIndustrial() {
  const [dias, setDias] = useState(30)

  const { data, isLoading } = useQuery<DashboardData>({
    queryKey: ['dashboard-ind', dias],
    queryFn: async () => (await api.get('/industrial/dashboard', { params: { dias } })).data,
    refetchInterval: 60000,
  })

  const { data: tend } = useQuery<TendenciasData>({
    queryKey: ['dashboard-ind-tend'],
    queryFn: async () => (await api.get('/industrial/dashboard/tendencias', { params: { meses: 6 } })).data,
  })

  const [analise, setAnalise] = useState<AnaliseIA | null>(null)
  const analisar = useMutation({
    mutationFn: async () => (await api.post('/industrial/ia/analise-custos')).data as AnaliseIA,
    onSuccess: (d) => {
      setAnalise(d)
      if (d.parse_erro) toast.error('Sofia respondeu mas não foi JSON válido — veja texto bruto')
      else toast.success('Análise gerada')
    },
    onError: (e: any) => {
      const msg = e.response?.data?.detail || 'Falha ao chamar Sofia'
      toast.error(msg)
    },
  })

  if (isLoading || !data) return <div className="p-6">Carregando dashboard…</div>

  const k = data.kpis

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Factory className="text-brand-600" /> Dashboard Industrial
          </h1>
          <p className="text-sm text-ink-600">
            Gestão de custos, perdas e capacidade · período de <strong>{dias} dias</strong>
          </p>
        </div>
        <div className="flex items-center gap-2">
          {[7, 30, 90, 180].map(d => (
            <button key={d} onClick={() => setDias(d)}
              className={`px-3 py-1 text-xs rounded ${dias === d ? 'bg-brand-600 text-white' : 'bg-ink-100 text-ink-700 hover:bg-ink-200'}`}>
              {d}d
            </button>
          ))}
          <Link to="/industrial/andon" className="btn-secondary text-xs ml-2">
            <Tv size={14} /> Painel TV
          </Link>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        <KPI label="OPs concluídas" value={k.ops_concluidas.toString()} />
        <KPI label="Custo real" value={formatMoney(k.custo_real_total)}
             sub={k.custo_previsto_total > 0 ? `previsto: ${formatMoney(k.custo_previsto_total)}` : 'sem dados de previsão'} />
        <KPI
          label="Desvio previsto vs real"
          value={k.desvio_pct === null ? '—' : `${k.desvio_pct > 0 ? '+' : ''}${k.desvio_pct}%`}
          accent={k.desvio_pct === null ? undefined : Math.abs(k.desvio_pct) > 10 ? 'rose' : 'emerald'}
          icon={k.desvio_pct !== null && k.desvio_pct > 0 ? TrendingUp : TrendingDown}
        />
        <KPI
          label="Taxa de refugo"
          value={k.taxa_refugo_pct === null ? '—' : `${k.taxa_refugo_pct}%`}
          accent={k.taxa_refugo_pct === null ? undefined : k.taxa_refugo_pct > 5 ? 'rose' : 'emerald'}
          sub={`${k.quantidade_refugo.toFixed(0)} de ${(k.quantidade_produzida + k.quantidade_refugo).toFixed(0)}`}
        />
        <KPI label="Propostas pendentes" value={k.propostas_pendentes.toString()} accent={k.propostas_pendentes > 0 ? 'amber' : undefined} />
      </div>

      {/* Breakdown de custo real */}
      {data.custo_breakdown && k.custo_real_total > 0 && (
        <div className="card card-body">
          <h3 className="font-semibold mb-3">Decomposição do custo real — {formatMoney(k.custo_real_total)}</h3>
          <CustoBreakdownBar breakdown={data.custo_breakdown} total={k.custo_real_total} />
        </div>
      )}

      {/* Alertas acionáveis */}
      {data.alertas.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
            <AlertTriangle className="text-amber-600" size={18} /> Alertas acionáveis
          </h2>
          <div className="space-y-2">
            {data.alertas.map((a, i) => (
              <div key={i} className={`border-l-4 rounded p-3 ${SEV_COR[a.severidade] || SEV_COR.baixa}`}>
                <div className="font-semibold">{a.titulo}</div>
                {a.descricao && <div className="text-sm mt-1">{a.descricao}</div>}
                {a.acao && <div className="text-xs mt-1 opacity-80">→ {a.acao}</div>}
                {a.itens && a.itens.length > 0 && (
                  <details className="mt-2 text-xs">
                    <summary className="cursor-pointer">ver {a.itens.length} item(ns)</summary>
                    <ul className="mt-1 space-y-0.5 font-mono">
                      {a.itens.slice(0, 10).map((it, j) => (
                        <li key={j}>{it.codigo} — {it.descricao}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Gráfico de tendência + tops */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card card-body">
          <h3 className="font-semibold mb-2">Custos mensais (previsto vs real)</h3>
          {tend && tend.serie.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={tend.serie}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mes" />
                <YAxis />
                <Tooltip formatter={(v: any) => formatMoney(Number(v))} />
                <Legend />
                <Line type="monotone" dataKey="custo_previsto" stroke="#6366f1" name="Previsto" />
                <Line type="monotone" dataKey="custo_real" stroke="#ef4444" name="Real" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-sm text-ink-500 py-8 text-center">Sem OPs concluídas no período.</div>
          )}
        </div>

        <div className="card card-body">
          <h3 className="font-semibold mb-2">Produção vs refugo (últimos meses)</h3>
          {tend && tend.serie.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={tend.serie}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mes" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="quantidade_produzida" fill="#10b981" name="OK" />
                <Bar dataKey="quantidade_refugo" fill="#ef4444" name="Refugo" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-sm text-ink-500 py-8 text-center">Sem dados.</div>
          )}
        </div>
      </div>

      {/* Tops */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card card-body">
          <h3 className="font-semibold mb-2">Top produtos por custo</h3>
          {data.top_custo.length === 0 ? (
            <div className="text-sm text-ink-500 py-4 text-center">Sem dados.</div>
          ) : (
            <table className="table text-sm">
              <thead><tr><th>Produto</th><th className="text-right">Qtd</th><th className="text-right">Custo unit.</th><th className="text-right">Custo total</th></tr></thead>
              <tbody>
                {data.top_custo.map(p => (
                  <tr key={p.produto_id}>
                    <td><div className="font-mono text-xs">{p.codigo}</div><div className="text-xs">{p.descricao}</div></td>
                    <td className="text-right">{p.quantidade.toFixed(0)}</td>
                    <td className="text-right">{formatMoney(p.custo_unit)}</td>
                    <td className="text-right font-semibold">{formatMoney(p.custo_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="card card-body">
          <h3 className="font-semibold mb-2">Top produtos por taxa de refugo</h3>
          {data.top_refugo.length === 0 ? (
            <div className="text-sm text-ink-500 py-4 text-center">Sem refugo no período.</div>
          ) : (
            <table className="table text-sm">
              <thead><tr><th>Produto</th><th className="text-right">OK</th><th className="text-right">Refugo</th><th className="text-right">%</th></tr></thead>
              <tbody>
                {data.top_refugo.map(p => (
                  <tr key={p.produto_id}>
                    <td><div className="font-mono text-xs">{p.codigo}</div><div className="text-xs">{p.descricao}</div></td>
                    <td className="text-right text-emerald-700">{p.ok.toFixed(0)}</td>
                    <td className="text-right text-rose-700">{p.refugo.toFixed(0)}</td>
                    <td className="text-right font-semibold text-rose-700">{p.taxa_refugo_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Painel IA */}
      <div className="card card-body border-brand-200 bg-gradient-to-br from-brand-50 to-sky-50">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Sparkles className="text-brand-600" size={20} /> Análise da Sofia — Gestão de custos
          </h2>
          <button onClick={() => analisar.mutate()} disabled={analisar.isPending}
            className="btn-primary">
            {analisar.isPending && <Loader2 size={14} className="animate-spin" />}
            {analisar.isPending ? 'Analisando… (até 40s)' : analise ? 'Re-analisar' : 'Analisar com a Sofia'}
          </button>
        </div>

        {!analise && !analisar.isPending && (
          <div className="text-sm text-ink-600">
            Clique em <strong>Analisar com a Sofia</strong> para receber insights acionáveis sobre seus custos, perdas e
            oportunidades de otimização. A IA considera OPs concluídas, refugo, fichas incompletas e cadastro de MP.
          </div>
        )}

        {analise?.parse_erro && analise.texto_bruto && (
          <div className="bg-rose-50 border border-rose-200 p-3 rounded text-xs">
            <div className="font-semibold mb-1">Resposta não estruturada:</div>
            <pre className="whitespace-pre-wrap">{analise.texto_bruto}</pre>
          </div>
        )}

        {analise?.analise && (
          <div className="space-y-4">
            <div className="p-3 bg-white rounded border">
              <div className="text-xs font-semibold text-ink-500 uppercase mb-1">Resumo</div>
              <div className="text-sm">{analise.analise.resumo}</div>
            </div>

            {analise.analise.pontos_fortes && analise.analise.pontos_fortes.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-1 flex items-center gap-1 text-emerald-700">
                  <CheckCircle size={14} /> Pontos fortes
                </h4>
                <ul className="text-sm space-y-0.5 list-disc list-inside text-ink-700">
                  {analise.analise.pontos_fortes.map((p, i) => <li key={i}>{p}</li>)}
                </ul>
              </div>
            )}

            {analise.analise.observacoes && analise.analise.observacoes.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-1 flex items-center gap-1">
                  <AlertTriangle size={14} className="text-amber-600" /> Observações
                </h4>
                <div className="space-y-2">
                  {analise.analise.observacoes.map((o, i) => (
                    <div key={i} className={`p-2 rounded border-l-4 ${SEV_COR[o.severidade] || SEV_COR.media}`}>
                      <div className="font-semibold text-sm">{o.titulo}</div>
                      <div className="text-xs mt-0.5">{o.detalhe}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {analise.analise.oportunidades && analise.analise.oportunidades.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-1 flex items-center gap-1">
                  <Lightbulb size={14} className="text-amber-500" /> Oportunidades
                </h4>
                <div className="grid md:grid-cols-2 gap-2">
                  {analise.analise.oportunidades.map((o, i) => (
                    <div key={i} className="p-3 bg-white rounded border">
                      <div className="font-semibold text-sm">{o.titulo}</div>
                      <div className="text-xs text-ink-600 mt-1">{o.descricao}</div>
                      <div className="flex gap-2 mt-2 text-xs">
                        <span className="bg-emerald-100 text-emerald-800 px-1.5 rounded">{o.impacto_estimado}</span>
                        <span className="bg-ink-100 text-ink-700 px-1.5 rounded">esforço: {o.esforco}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {analise.analise.acoes_priorizadas && analise.analise.acoes_priorizadas.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-1 flex items-center gap-1">
                  <Target size={14} className="text-brand-600" /> Ações priorizadas
                </h4>
                <ol className="space-y-1">
                  {analise.analise.acoes_priorizadas.map((a, i) => (
                    <li key={i} className="p-2 bg-white rounded border flex gap-2">
                      <div className="font-bold text-brand-600">{a.ordem}.</div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold">{a.acao}</div>
                        <div className="text-xs text-ink-600">{a.motivo}</div>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {analise.uso && (
              <div className="text-xs text-ink-400 text-right">
                modelo: {analise.modelo} ·
                tokens in/out: {analise.uso.input_tokens}/{analise.uso.output_tokens}
                {analise.uso.cache_read_input_tokens ? ` · cache hit: ${analise.uso.cache_read_input_tokens}` : ''}
                {analise.contexto_resumo ? ` · OPs: ${analise.contexto_resumo.ops_analisadas}, fichas incompletas: ${analise.contexto_resumo.fichas_incompletas}` : ''}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function CustoBreakdownBar({ breakdown, total }: { breakdown: DashboardData['custo_breakdown']; total: number }) {
  const partes = [
    { label: 'Matéria-prima', key: 'mp', valor: breakdown.mp, cor: 'bg-emerald-500' },
    { label: 'Mão de obra', key: 'mod', valor: breakdown.mod, cor: 'bg-sky-500' },
    { label: 'Hora-máquina', key: 'maquina', valor: breakdown.maquina, cor: 'bg-amber-500' },
    { label: 'Overhead', key: 'overhead', valor: breakdown.overhead, cor: 'bg-violet-500' },
  ]
  return (
    <div className="space-y-3">
      <div className="flex h-8 rounded overflow-hidden border">
        {partes.map(p => {
          const pct = total > 0 ? (p.valor / total) * 100 : 0
          if (pct <= 0) return null
          return (
            <div key={p.key} className={`${p.cor} flex items-center justify-center text-white text-xs font-semibold`}
              style={{ width: `${pct}%` }} title={`${p.label}: ${formatMoney(p.valor)} (${pct.toFixed(1)}%)`}>
              {pct > 8 ? `${pct.toFixed(0)}%` : ''}
            </div>
          )
        })}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
        {partes.map(p => (
          <div key={p.key} className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-sm ${p.cor} shrink-0`} />
            <div className="flex-1">
              <div className="text-ink-600 text-xs">{p.label}</div>
              <div className="font-semibold">{formatMoney(p.valor)}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function KPI({ label, value, sub, accent, icon: Icon }: {
  label: string; value: string; sub?: string;
  accent?: 'emerald' | 'rose' | 'amber'; icon?: any
}) {
  const colors: Record<string, string> = {
    emerald: 'text-emerald-700',
    rose: 'text-rose-700',
    amber: 'text-amber-700',
  }
  return (
    <div className="card card-body">
      <div className="text-xs text-ink-500 uppercase flex items-center gap-1">
        {Icon && <Icon size={12} />} {label}
      </div>
      <div className={`text-xl font-bold ${accent ? colors[accent] : ''}`}>{value}</div>
      {sub && <div className="text-xs text-ink-500 mt-0.5">{sub}</div>}
    </div>
  )
}
